import { useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';
import { useSessionState } from '../lib/session-context.tsx';

type State =
  | { phase: 'needs-sign-in' }
  | { phase: 'accepting' }
  | { phase: 'done'; companyName: string }
  | { phase: 'failed'; message: string };

/**
 * Redeem an invite.
 *
 * The token is deliberately handled server-side: the recipient cannot read
 * the invite collection, so there is nothing here that reveals who else was
 * invited or what other companies exist.
 */
export function AcceptInvite() {
  const ports = usePorts();
  const { token } = useParams({ from: '/invite/$token' });
  const session = useSessionState();
  const [state, setState] = useState<State>({ phase: 'accepting' });
  const attempted = useRef(false);

  useEffect(() => {
    if (session.status === 'loading') return;
    if (session.status === 'signed-out') {
      setState({ phase: 'needs-sign-in' });
      return;
    }
    // Guard against a second attempt: accepting twice fails by design, and
    // showing that error to someone who did nothing wrong would be confusing.
    if (attempted.current) return;
    attempted.current = true;

    setState({ phase: 'accepting' });
    void ports.functions
      .call('acceptInvite', { token })
      .then((result) => {
        setState({ phase: 'done', companyName: result.companyName });
        // Reload so the session picks up the new membership from scratch.
        globalThis.setTimeout(() => globalThis.location?.assign('/app'), 1200);
      })
      .catch((cause: unknown) => {
        setState({
          phase: 'failed',
          message: cause instanceof Error ? cause.message : 'That invite could not be used',
        });
      });
  }, [ports, token, session.status]);

  return (
    <main className="grid min-h-full place-items-center p-6 text-center">
      <div className="max-w-sm">
        {state.phase === 'needs-sign-in' && (
          <>
            <h1 className="text-lg font-semibold text-slate-900">Create your account</h1>
            <p className="mt-1 text-sm text-slate-600">
              Use the address this invite was sent to. You will come straight back here — and if you
              already have an account, there is a link to sign in instead.
            </p>
            {/*
              The token goes with them.

              It did not, and the sentence above used to end "then open this
              link again" — which was the product admitting it had lost
              something the person then had to go and find. Worse, the form
              they arrived at could only create an account **with a new
              company**, so an invitee without an account had no way
              through at all. Both halves reported from hand-testing on dev.
            */}
            <Button
              className="mt-4"
              onClick={() =>
                globalThis.location?.assign(`/signin?invite=${encodeURIComponent(token)}`)
              }
            >
              Continue
            </Button>
          </>
        )}

        {state.phase === 'accepting' && <p className="text-sm text-slate-500">Accepting invite…</p>}

        {state.phase === 'done' && (
          <>
            <h1 className="text-lg font-semibold text-slate-900">You are in</h1>
            <p className="mt-1 text-sm text-slate-600">
              Joined {state.companyName}. Taking you there now.
            </p>
          </>
        )}

        {state.phase === 'failed' && (
          <>
            <h1 className="text-lg font-semibold text-slate-900">That invite did not work</h1>
            <p role="alert" className="mt-1 text-sm text-slate-600">
              {state.message}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Invites expire after 14 days and can only be used once. Ask for a fresh one.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
